import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Alert,
} from '@mui/material';
import { Assessment, Send } from '@mui/icons-material';

interface RiskScore {
  condition: string;
  risk: number;
  confidence: number;
  color: string;
}

const RiskAssessment: React.FC = () => {
  const [formData, setFormData] = useState({
    age: '',
    gender: '',
    state: '',
    insuranceType: '',
    urbanRural: '',
    symptomCount: '',
    symptomSeverity: '',
    symptoms: '',
  });

  const [riskScores, setRiskScores] = useState<RiskScore[]>([]);
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name as string]: value,
    }));
  };

  const calculateRiskScores = (): RiskScore[] => {
    const age = parseInt(formData.age);
    const symptomCount = parseInt(formData.symptomCount);
    const symptomSeverity = parseInt(formData.symptomSeverity);
    const symptoms = formData.symptoms.toLowerCase();

    // Base risk factors
    const genderFactor = formData.gender === 'Female' ? 1.3 : 1.0;
    const ageFactor = age > 40 ? 1.2 : age > 30 ? 1.1 : 1.0;
    const insuranceFactor = formData.insuranceType === 'Uninsured' ? 1.3 :
                           formData.insuranceType === 'Medicaid' ? 1.2 : 1.0;
    const locationFactor = formData.urbanRural === 'Rural' ? 1.25 :
                          formData.urbanRural === 'Suburban' ? 1.1 : 1.0;
    const severityFactor = symptomSeverity / 10;

    // Symptom keyword matching for different conditions
    const hasChronicPain = symptoms.includes('pain') || symptoms.includes('ache');
    const hasFatigue = symptoms.includes('fatigue') || symptoms.includes('tired') || symptoms.includes('exhausted');
    const hasCognitive = symptoms.includes('cognitive') || symptoms.includes('brain fog') || symptoms.includes('memory');
    const hasPelvic = symptoms.includes('pelvic') || symptoms.includes('menstrual') || symptoms.includes('period');
    const hasDizziness = symptoms.includes('dizzy') || symptoms.includes('lightheaded') || symptoms.includes('faint');
    const hasJoint = symptoms.includes('joint') || symptoms.includes('stiff') || symptoms.includes('swelling');
    const hasSleep = symptoms.includes('sleep') || symptoms.includes('insomnia');

    // Calculate risk for each condition
    const conditions = [
      {
        condition: 'Fibromyalgia',
        baseRisk: 30,
        factors: [
          hasChronicPain ? 25 : 0,
          hasFatigue ? 20 : 0,
          hasCognitive ? 15 : 0,
          hasSleep ? 10 : 0,
          genderFactor > 1 ? 15 : 0,
          symptomCount > 5 ? 10 : 0,
        ],
        color: '#dc004e',
      },
      {
        condition: 'Chronic Fatigue Syndrome',
        baseRisk: 25,
        factors: [
          hasFatigue ? 30 : 0,
          hasCognitive ? 20 : 0,
          hasSleep ? 15 : 0,
          hasChronicPain ? 10 : 0,
          symptomSeverity > 7 ? 15 : 0,
          symptomCount > 4 ? 10 : 0,
        ],
        color: '#ed6c02',
      },
      {
        condition: 'Endometriosis',
        baseRisk: formData.gender === 'Female' ? 20 : 0,
        factors: [
          hasPelvic ? 35 : 0,
          hasChronicPain ? 20 : 0,
          formData.gender === 'Female' && age >= 25 && age <= 40 ? 20 : 0,
          symptomSeverity > 6 ? 15 : 0,
        ],
        color: '#1976d2',
      },
      {
        condition: 'Autoimmune Disorder',
        baseRisk: 20,
        factors: [
          hasJoint ? 25 : 0,
          hasFatigue ? 20 : 0,
          hasChronicPain ? 15 : 0,
          genderFactor > 1 ? 15 : 0,
          symptomCount > 6 ? 15 : 0,
          age > 40 ? 10 : 0,
        ],
        color: '#2e7d32',
      },
      {
        condition: 'POTS',
        baseRisk: 15,
        factors: [
          hasDizziness ? 30 : 0,
          hasFatigue ? 20 : 0,
          hasCognitive ? 15 : 0,
          genderFactor > 1 ? 15 : 0,
          age < 40 ? 10 : 0,
          symptomSeverity > 6 ? 10 : 0,
        ],
        color: '#9c27b0',
      },
    ];

    // Calculate final scores
    const scores: RiskScore[] = conditions.map(cond => {
      const factorSum = cond.factors.reduce((sum, f) => sum + f, 0);
      let risk = cond.baseRisk + factorSum;
      
      // Apply multipliers
      risk *= genderFactor * ageFactor * insuranceFactor * locationFactor * severityFactor;
      
      // Cap at 95% and floor at 5%
      risk = Math.min(Math.max(Math.round(risk), 5), 95);
      
      // Calculate confidence based on symptom specificity
      const specificityScore = cond.factors.filter(f => f > 0).length;
      const confidence = Math.min(50 + (specificityScore * 10) + (symptomCount * 2), 95);
      
      return {
        condition: cond.condition,
        risk,
        confidence: Math.round(confidence),
        color: cond.color,
      };
    });

    // Sort by risk score descending
    return scores.sort((a, b) => b.risk - a.risk);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Simulate API processing time
    setTimeout(() => {
      const calculatedScores = calculateRiskScores();
      setRiskScores(calculatedScores);
      setLoading(false);
    }, 1500);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
          <Assessment sx={{ mr: 2, verticalAlign: 'middle' }} />
          Risk Assessment Tool
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          AI-powered risk assessment for invisible illnesses based on symptoms and demographics
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Input Form */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Patient Information
            </Typography>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Age"
                    name="age"
                    type="number"
                    value={formData.age}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Gender</InputLabel>
                    <Select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      label="Gender"
                    >
                      <MenuItem value="Female">Female</MenuItem>
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Non-binary">Non-binary</MenuItem>
                      <MenuItem value="Prefer not to say">Prefer not to say</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="State"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    placeholder="e.g., CA, NY, TX"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Insurance Type</InputLabel>
                    <Select
                      name="insuranceType"
                      value={formData.insuranceType}
                      onChange={handleInputChange}
                      label="Insurance Type"
                    >
                      <MenuItem value="Private">Private</MenuItem>
                      <MenuItem value="Medicare">Medicare</MenuItem>
                      <MenuItem value="Medicaid">Medicaid</MenuItem>
                      <MenuItem value="Uninsured">Uninsured</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Location Type</InputLabel>
                    <Select
                      name="urbanRural"
                      value={formData.urbanRural}
                      onChange={handleInputChange}
                      label="Location Type"
                    >
                      <MenuItem value="Urban">Urban</MenuItem>
                      <MenuItem value="Suburban">Suburban</MenuItem>
                      <MenuItem value="Rural">Rural</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Number of Symptoms"
                    name="symptomCount"
                    type="number"
                    value={formData.symptomCount}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Symptom Severity (1-10)"
                    name="symptomSeverity"
                    type="number"
                    value={formData.symptomSeverity}
                    onChange={handleInputChange}
                    inputProps={{ min: 1, max: 10 }}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Primary Symptoms"
                    name="symptoms"
                    value={formData.symptoms}
                    onChange={handleInputChange}
                    multiline
                    rows={3}
                    placeholder="e.g., chronic fatigue, widespread pain, cognitive issues"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    startIcon={<Send />}
                    disabled={loading}
                  >
                    {loading ? 'Analyzing...' : 'Assess Risk'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>

        {/* Results */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, minHeight: '600px' }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Risk Assessment Results
            </Typography>
            {loading && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress />
                <Typography variant="body2" color="textSecondary" sx={{ mt: 2, textAlign: 'center' }}>
                  Analyzing patient data with AI models...
                </Typography>
              </Box>
            )}
            {!loading && riskScores.length === 0 && (
              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="textSecondary">
                  Enter patient information and click "Assess Risk" to see results
                </Typography>
              </Box>
            )}
            {!loading && riskScores.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="info" sx={{ mb: 3 }}>
                  Risk scores are based on AI analysis of symptoms, demographics, and historical patterns
                </Alert>
                {riskScores.map((score, index) => (
                  <Card key={index} sx={{ mb: 2 }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="h6" fontWeight="bold">
                          {score.condition}
                        </Typography>
                        <Chip
                          label={`${score.risk}% Risk`}
                          sx={{
                            backgroundColor: score.color,
                            color: 'white',
                            fontWeight: 'bold',
                          }}
                        />
                      </Box>
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Risk Level
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={score.risk}
                          sx={{
                            height: 10,
                            borderRadius: 5,
                            backgroundColor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: score.color,
                            },
                          }}
                        />
                      </Box>
                      <Typography variant="body2" color="textSecondary">
                        Confidence: {score.confidence}%
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
                <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight="bold" gutterBottom>
                    Recommended Actions:
                  </Typography>
                  <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                    {riskScores[0]?.risk > 70 && (
                      <li><strong>Urgent:</strong> Schedule appointment with specialist within 2 weeks</li>
                    )}
                    {riskScores[0]?.condition === 'Fibromyalgia' && (
                      <li>Consult with a rheumatologist specializing in fibromyalgia</li>
                    )}
                    {riskScores[0]?.condition === 'Chronic Fatigue Syndrome' && (
                      <li>Seek evaluation from a CFS/ME specialist or infectious disease doctor</li>
                    )}
                    {riskScores[0]?.condition === 'Endometriosis' && (
                      <li>Schedule consultation with gynecologist or endometriosis specialist</li>
                    )}
                    {riskScores[0]?.condition === 'Autoimmune Disorder' && (
                      <li>Request comprehensive autoimmune panel and ANA test</li>
                    )}
                    {riskScores[0]?.condition === 'POTS' && (
                      <li>Consult with cardiologist or autonomic specialist for tilt table test</li>
                    )}
                    <li>Keep detailed symptom diary for 2-4 weeks including triggers and patterns</li>
                    <li>Request comprehensive blood work and relevant imaging studies</li>
                    {parseInt(formData.symptomSeverity) > 7 && (
                      <li>Consider referral to pain management or palliative care specialist</li>
                    )}
                    {formData.urbanRural === 'Rural' && (
                      <li>Explore telemedicine options for specialist consultations</li>
                    )}
                    {formData.insuranceType === 'Uninsured' && (
                      <li>Contact patient advocacy groups for financial assistance resources</li>
                    )}
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default RiskAssessment;

// Made with Bob
